import { getLogger, getMeter, type OtterLogger } from '@otter-ai/telemetry';

// Lazy initialization
let ssrOutcomeCounter: ReturnType<
  ReturnType<typeof getMeter>['createCounter']
> | null = null;
let ssrLogger: OtterLogger | null = null;

function getSSROutcomeCounter() {
  if (!ssrOutcomeCounter) {
    const meter = getMeter('ssr');
    /** Count of SSR render outcomes by route, outcome type, and plan */
    ssrOutcomeCounter = meter.createCounter('render.outcome.count');
  }
  return ssrOutcomeCounter;
}

function getSSRLogger() {
  if (!ssrLogger) {
    ssrLogger = getLogger('ssr');
  }
  return ssrLogger;
}

type SSROutcome = 'success' | 'not_found' | 'error';

type SSROutcomeAttributes = {
  Route: string;
  Outcome: SSROutcome;
  Plan: string;
};

type SSRErrorLogAttributes = {
  Route: string;
  Plan: string;
  ErrorType: string;
  ErrorMessage: string;
};

/**
 * Records an SSR render outcome
 */
export function recordSSROutcome(
  outcome: SSROutcome,
  attributes: Omit<SSROutcomeAttributes, 'Outcome'>,
) {
  getSSROutcomeCounter().add(1, {
    ...attributes,
    Outcome: outcome,
  });
}

/**
 * Logs SSR_RenderFailed for render errors (distinct from prefetch errors)
 */
export function logSSRRenderFailed(attributes: SSRErrorLogAttributes) {
  getSSRLogger().error({
    eventName: 'SSR_RenderFailed',
    attributes,
  });
}

/**
 * Logs SSR_PrefetchFailed for data-loading errors during React Query prefetch
 */
export function logSSRPrefetchFailed(
  attributes: SSRErrorLogAttributes & { QueryName?: string },
) {
  getSSRLogger().error({
    eventName: 'SSR_PrefetchFailed',
    attributes,
  });
}

/**
 * Helper to get error type classification
 */
export function getErrorType(error: unknown): string {
  if (error instanceof Error) {
    if (error.name === 'TypeError') return 'TypeError';
    if (error.name === 'SyntaxError') return 'SyntaxError';
    if (error.name === 'ReferenceError') return 'ReferenceError';
    if (error.message.includes('fetch')) return 'FetchError';
    if (error.message.includes('network')) return 'NetworkError';
    if (error.message.includes('timeout')) return 'TimeoutError';
  }
  return 'UnknownError';
}

/**
 * Helper to get error message safely
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return typeof error === 'string' ? error : 'Unknown error';
}
